<template>
  <div class="contents" data-test="lazy-load-wrapper">
    <div ref="observerTarget" class="h-[1px]" />

    <div
      v-if="isLoading"
      class="my-5 text-center"
      data-test="lazy-load-loading-indicator"
    >
      <RevSpinner
        :alternative-text="i18n(translations.loading)"
        data-test="lazy-load-spinner"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import { RevSpinner } from '@ds/components/Spinner'

import { LAZYLOAD_DEBOUNCE_DELAY } from './LazyLoad.constants'
import translations from './LazyLoad.translations'

interface Props {
  isLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
})

const emit = defineEmits(['load-more'])

const observer = ref<IntersectionObserver>()
const observerTarget = ref<null | HTMLElement>(null)
const target = ref<IntersectionObserverEntry>()

const i18n = useI18n()

const handleIntersection = (entries: IntersectionObserverEntry[]) => {
  target.value = entries[0]

  if (!props.isLoading && target.value.isIntersecting) {
    emit('load-more')
  }
}

const debounceHandleIntersection = useDebounceFn(
  function debouncedIsLoadingWatch(isLoading) {
    if (isLoading === false && target.value?.isIntersecting) {
      emit('load-more')
    }
  },
  LAZYLOAD_DEBOUNCE_DELAY,
)

onMounted(() => {
  observer.value = new IntersectionObserver(handleIntersection)

  if (observerTarget.value) observer.value.observe(observerTarget.value)
})

onBeforeUnmount(() => {
  observer.value?.disconnect()
})

watch(
  () => props.isLoading,
  () => {
    debounceHandleIntersection(props.isLoading)
  },
)
</script>
